import {Injectable} from '@angular/core';
import {AuthSessionApiService, LivenessFrontendErrorRequest} from '@npck/auth-frontend-api';
import {SentryService, TracingService} from '@npck/libs/commons';
import {timeout} from 'rxjs/operators';
import {AuthHolderService} from './auth-holder.service';

const FRONTEND_ERROR_REPORT_TIMEOUT_MS = 3_000;
const LIVENESS_PROCESS_TIMEOUT_MS = 30_000;

@Injectable({
    providedIn: 'root',
})
export class LivenessService {

    constructor(
        private tracingService: TracingService,
        private sentryService: SentryService,
        private authSessionApiService: AuthSessionApiService,
        private authHolderService: AuthHolderService,
    ) {
    }

    reportFrontendError(payload: Omit<LivenessFrontendErrorRequest, 'sessionId'>): void {
        const sessionId = this.authHolderService.getCurrentAuthSessionId();
        if (!sessionId) {
            return;
        }
        this.authSessionApiService.reportLivenessFrontendError({...payload, sessionId})
            .pipe(timeout(FRONTEND_ERROR_REPORT_TIMEOUT_MS))
            .subscribe({
                error: e => this.sentryService.sendErrorMessage(`reportLivenessFrontendError failed: ${e?.message ?? e}`, scope => {
                    scope.setTag('reportLivenessFrontendErrorFailed', true);
                }),
            });
    }

    logError(state: any, errorSource?: string): void {
        // FaceTecSDK грузится лениво — глобала может не быть, если сам скрипт не загрузился
        const sdkLoaded = typeof FaceTecSDK !== 'undefined';
        const livenessStatus = sdkLoaded ? FaceTecSDK.getStatus() : undefined;
        const statusCode = sdkLoaded ? FaceTecSDK.FaceTecSDKStatus[livenessStatus] : 'SDK_NOT_LOADED';
        const statusDescription = sdkLoaded
            ? FaceTecSDK.getFriendlyDescriptionForFaceTecSDKStatus(livenessStatus)
            : 'SDK_NOT_LOADED';
        this.tracingService.addNewSpan(`FaceTecSdk status: ${state}`, span => {
            span.setAttribute('errorMessage', state);
            span.setAttribute('livenessStatus', livenessStatus);
            span.setAttribute('livenessDescription', statusDescription);
            span.setAttribute('errorSource', errorSource);
            span.setAttribute('error', true);
        });
        this.sentryService.sendErrorMessage(`FaceTecSdk status: ${state}`, scope => {
            scope.setTag('errorState', state);
            scope.setTag('livenessStatus', livenessStatus);
            scope.setTag('livenessDescription', statusDescription);
            scope.setTag('errorSource', errorSource);
        });
        this.reportFrontendError({
            status: String(state),
            errorCode: statusCode,
            errorDescription: errorSource ?? statusDescription,
        });
    }

    createTimeoutReporter(): {cancelTimeout: () => void} {
        const startedAt = Date.now();
        let isResponding = false;

        const timeoutId = setTimeout(() => {
            if (!isResponding) {
                this.sentryService.sendErrorMessage(`Liveness Process Timeout Detected`, scope => {
                    scope.setTag('livenessTimeout', true);
                    scope.setTag('faceTecSdkStatus', FaceTecSDK.getStatus());
                    scope.setLevel('error');
                });
                this.reportFrontendError({
                    status: 'TIMEOUT_30S',
                    errorCode: FaceTecSDK.FaceTecSDKStatus[FaceTecSDK.getStatus()],
                    errorDescription: 'Liveness Process Timeout Detected',
                    durationMs: Date.now() - startedAt,
                });
            }
        }, LIVENESS_PROCESS_TIMEOUT_MS);

        return {
            cancelTimeout: () => {
                isResponding = true;
                clearTimeout(timeoutId);
            },
        };
    }

    logOzError(state: string, errorMessage?: string): void {
        this.tracingService.addNewSpan(`OzLiveness status: ${state}`, span => {
            span.setAttribute('errorMessage', state);
            if (errorMessage) {
                span.setAttribute('errorDetail', errorMessage);
            }
            span.setAttribute('error', true);
        });
        this.sentryService.sendErrorMessage(`OzLiveness status: ${state}`, scope => {
            scope.setTag('errorState', state);
            if (errorMessage) {
                scope.setTag('errorDetail', errorMessage);
            }
        });
    }

    addBreadcrumb(message: string, data?: {[key: string]: any}): void {
        this.sentryService.addBreadcrumb({message: message, data: data});
    }

}
